import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import TreasureChestScene, {
  getTreasureRevealTimings,
  TREASURE_RARITY_COLORS,
} from './TreasureChestScene';
import { RELICS } from './relics';
import RelicIcon from './RelicIcon';
import { playSound } from '../../utils/utils';
import './BoardTreasureReveal.css';
import { usePopupTransition } from './popupTransition';
import { usesMobileRendering } from './mobileRendering';

const BoardTreasureReveal = ({
  reward,
  stage,
  activationText,
  reduceMotion,
  onComplete,
}) => {
  const { close, popupClass } = usePopupTransition(undefined, {
    reduceMotion,
    transitionKey: `${reward?.id}:${stage}`,
  });
  const relic = RELICS[reward?.id];
  const [cue, setCue] = useState(reduceMotion ? 'complete' : 'sealed');
  const dialogRef = useRef(null);
  const claimRef = useRef(null);
  const claimedRef = useRef(false);
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;
  const rarity = relic?.category || reward?.rarity || relic?.rarity || 'Common';
  const special = rarity === 'Special';
  const revealTimings = getTreasureRevealTimings(rarity);
  const revealed = cue === 'revealed' || cue === 'complete';
  const ready = cue === 'complete';

  useEffect(() => {
    if (stage !== 1 || !relic) return undefined;
    claimedRef.current = false;
    setCue(reduceMotion ? 'complete' : 'sealed');
    if (reduceMotion) return undefined;
    if (special)
      playSound('/sounds/epic-get-ready.mp3', { preventOverlap: true });
    playSound('/sounds/treasure_chest_reveal.mp3', { preventOverlap: true });
    const timers = [
      setTimeout(() => {
        playSound('/sounds/treasure-chest-slice-open.mp3', {
          preventOverlap: true,
        });
      }, revealTimings.slice),
      setTimeout(() => {
        setCue('opening');
      }, revealTimings.unlock),
      setTimeout(() => setCue('rising'), revealTimings.rise),
      setTimeout(() => {
        setCue('revealed');
        playSound('/sounds/relic-obtained.mp3');
      }, revealTimings.reveal),
      setTimeout(() => setCue('complete'), revealTimings.complete),
    ];
    return () => timers.forEach(clearTimeout);
  }, [stage, relic, reduceMotion, special, revealTimings]);

  useEffect(() => {
    if (stage !== 1 || !relic) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus({ preventScroll: true });
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected)
        previousFocus.focus({ preventScroll: true });
    };
  }, [stage, relic]);

  useEffect(() => {
    if (stage === 1 && ready) claimRef.current?.focus({ preventScroll: true });
  }, [stage, ready]);

  if (!relic) return null;
  const rarityClass = rarity.toLowerCase();
  if (stage !== 1) {
    return (
      <motion.div
        className={`coreboundTreasureReveal coreboundRarityCard-${rarityClass} ${
          special ? 'is-special-reveal' : ''
        }`}
        role="status"
        initial={reduceMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <span
          className={`coreboundTreasureRevealIcon coreboundRelicIcon-${relic.animation}`}
          aria-hidden="true"
        >
          <RelicIcon relicId={relic.id} icon={relic.icon} />
        </span>
        <span
          className={`coreboundTreasureRevealRarity coreboundRarity-${rarityClass}`}
        >
          {rarity.toUpperCase()} RELIC
        </span>
        <strong>{relic.name}</strong>
        <p>{relic.description}</p>
        <small>{activationText || 'RELIC ACQUIRED · EFFECT READY'}</small>
      </motion.div>
    );
  }

  const claim = () => {
    if (!ready || claimedRef.current) return;
    claimedRef.current = true;
    playSound('/sounds/menu-select-2.mp3');
    close(() => completeRef.current?.());
  };
  const title = {
    sealed: 'Something is waking…',
    opening: 'The seals are breaking…',
    rising: 'A hidden power is rising…',
    revealed: special
      ? 'A Special relic is revealed.'
      : 'Your relic is revealed.',
    complete: special ? 'A rare power is yours.' : 'A new power is yours.',
  }[cue];

  return createPortal(
    <div
      ref={dialogRef}
      className={`coreboundTreasureCinematic ${popupClass} at-${cue} ${
        reduceMotion ? 'is-reduced' : ''
      } ${special ? 'is-special-reveal' : ''} ${
        usesMobileRendering() ? 'is-mobile-rendering' : ''
      }`}
      style={{ '--treasure-color': TREASURE_RARITY_COLORS[rarity] }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="coreboundTreasureTitle"
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key === 'Tab') {
          event.preventDefault();
          if (ready) claimRef.current?.focus();
          else dialogRef.current?.focus();
        }
        if (event.key === 'Escape' && ready) {
          event.preventDefault();
          claim();
        }
      }}
    >
      <TreasureChestScene
        relic={relic}
        rarity={rarity}
        reduceMotion={reduceMotion}
      />
      <div className="coreboundTreasureCinematicHeader">
        <span>
          {revealed
            ? special
              ? 'SPECIAL SIGNAL RECOVERED / CACHE OPENED'
              : 'SIGNAL RECOVERED / CACHE OPENED'
            : special
            ? 'SEALED CACHE / RARE SIGNAL DETECTED'
            : 'SEALED CACHE / UNKNOWN SIGNAL'}
        </span>
        <h2 id="coreboundTreasureTitle">{title}</h2>
      </div>
      <div className="coreboundTreasureCinematicFooter" aria-live="polite">
        {revealed ? (
          <div className="coreboundTreasureLootDetails">
            <span className={`coreboundRarity-${rarityClass}`}>
              {rarity.toUpperCase()} RELIC
            </span>
            <h3>{relic.name}</h3>
            <p>{relic.description}</p>
          </div>
        ) : (
          <p className="coreboundTreasureMystery">
            {special && cue === 'sealed'
              ? 'An unknown power is resonating beyond the vault seals…'
              : cue === 'sealed'
              ? 'A forgotten relic is stirring inside the vault.'
              : cue === 'opening'
              ? 'Light is escaping through the ancient seals.'
              : 'The cache is releasing its hidden treasure…'}
          </p>
        )}
        <button
          ref={claimRef}
          type="button"
          className="coreboundPrimary coreboundTreasureClaim"
          disabled={!ready}
          onClick={claim}
        >
          {ready ? 'Claim relic →' : 'Decoding the relic…'}
        </button>
      </div>
    </div>,
    document.body
  );
};

export default BoardTreasureReveal;
